import {
  Box,
  Button,
  Card,
  CardHeader,
  Divider,
  Skeleton,
  Stack,
  Step,
  StepContent,
  StepLabel,
  Stepper,
  styled,
  Typography,
  useTheme,
} from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { DateTime } from "luxon";
import { useConfirm } from "material-ui-confirm";
import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router";
import { AddIssueOrTaskButton, HeaderLayout, MeetingHeader, SignalRGroup, TextViewer } from "../../components";
import { Links, MeetingAgendaItemType, MeetingType } from "../../constants";
import { useSession, useSignalR } from "../../hooks";
import { queryKeys, useEndMeeting, useGetMeeting, useGetMeetingParticipants } from "../../http";
import { MeetingAgendaStepContent, MeetingNotesButton } from "./components";
import { UserFeedbackAgendaStepperContent } from "./components/UserFeedbackAgendaStepperContent";

const StickySideMenuCard = styled(Card)`
  position: sticky;
  top: 176px;
  overflow: visible;
  z-index: 2;
  min-width: 300px;
  min-height: 0;
  height: fit-content;
`;

interface UrlParams {
  meetingGuid: string;
}

const MeetingPage = () => {
  const { meetingGuid } = useParams<keyof UrlParams>() as UrlParams;
  const sessionUser = useSession();
  const [, setMeetingIsClosing] = useState(false);
  const { data: meeting, isLoading: isLoading } = useGetMeeting(meetingGuid, true);
  const { data: currentParticipants } = useGetMeetingParticipants(meetingGuid, true);
  const { mutate: endMeeting, isLoading: isMeetingEnding } = useEndMeeting();
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeStepIndex, setActiveStepIndex] = useState<number>(0);
  const theme = useTheme();
  const confirm = useConfirm();
  const navigate = useNavigate();
  const signalR = useSignalR();
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!meeting) {
      return;
    }

    const stepFromUrl = parseInt(searchParams.get("agendaStep") ?? "0", 10);
    const newStepIndex =
      !isNaN(stepFromUrl) && stepFromUrl >= 0 && stepFromUrl < meeting.agendaItems.length ? stepFromUrl : 0;

    if (newStepIndex !== activeStepIndex) {
      setActiveStepIndex(newStepIndex);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, meeting]);

  useEffect(() => {
    signalR.joinGroup(SignalRGroup.Meeting(meetingGuid));
    return () => signalR.leaveGroup(SignalRGroup.Meeting(meetingGuid));
  }, [meetingGuid, signalR]);

  useEffect(() => {
    const handleShouldRefetchParticipants = (meetingGuid: string) => {
      queryClient.invalidateQueries(queryKeys.meetings.getMeetingParticipants(meetingGuid));
    };

    signalR.onShouldRefetchMeetingParticipants(handleShouldRefetchParticipants);
    return () => signalR.offShouldRefetchMeetingParticipants(handleShouldRefetchParticipants);
  }, [queryClient, signalR]);

  useEffect(() => {
    const handleShouldEndMeeting = () => {
      queryClient.invalidateQueries(queryKeys.meetings.getMeeting(meetingGuid));

      // Force these queries to reload because if the user clicks the "Go To Meetings Page" button
      // we don't want a flash of old meeting instance state.
      queryClient.removeQueries(queryKeys.meetings.getMeetingsForAllTeams());
      queryClient.removeQueries(queryKeys.meetings.getOneOnOneMeetingsForAllUsers());
    };

    signalR.onShouldEndMeeting(handleShouldEndMeeting);
    return () => signalR.offShouldEndMeeting(handleShouldEndMeeting);
  }, [meetingGuid, queryClient, signalR]);

  function handleActiveAgendaStepChanged(newAgendaStepIndex: number) {
    if (meeting && newAgendaStepIndex >= 0 && newAgendaStepIndex < meeting.agendaItems.length) {
      // Update the URL. The useEffect above will handle setting the state.
      setSearchParams({ agendaStep: newAgendaStepIndex.toString() }, { replace: true });
    }
  }

  async function handleCloseMeetingForAll() {
    if (!meeting || !meeting.mostRecentInstance) {
      return;
    }

    const { confirmed } = await confirm({
      title: "Close Meeting",
      content: "Are you sure you want to close the meeting for all participants?",
      confirmationText: "OK",
      cancellationText: "Cancel",
    });

    if (!confirmed) {
      return;
    }

    setMeetingIsClosing(true);

    endMeeting(
      { meetingGuid: meetingGuid },
      {
        onSuccess: () => {
          signalR.sendRefetchMeetingListItem(meetingGuid);
          signalR.sendEndMeeting(meetingGuid);

          if (meeting?.type === MeetingType.Team) {
            navigate(Links.TeamMeetings(meeting.team.slug));
          } else {
            navigate(Links.UserMeetings(sessionUser.userId));
          }
        },
      },
    );
  }

  if (isLoading || meeting == null) {
    return (
      <HeaderLayout
        title={
          <Stack spacing={1}>
            <Skeleton variant="rectangular" height={40} width={400} />
            <Skeleton variant="rectangular" height={24} width={500} />
          </Stack>
        }
      >
        <Card sx={{ flex: 1 }}>
          <CardHeader title={<Skeleton height={28} width={200} />} />
          <Divider />
          <Stack sx={{ p: 1 }}>
            <Skeleton />
            <Skeleton />
            <Skeleton />
          </Stack>
        </Card>
        <Box sx={{ flex: 2 }}></Box>
      </HeaderLayout>
    );
  }

  if (meeting.mostRecentInstance == null || meeting.mostRecentInstance.endedAtDateTimeUtc != null) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100vh", width: "100%" }}>
        <Stack spacing={1} sx={{ textAlign: "center", maxWidth: 400 }}>
          <Typography variant="h4">Meeting has ended.</Typography>

          {meeting.mostRecentInstance && (
            <>
              <Typography>
                <strong>
                  {meeting.type === MeetingType.Team
                    ? meeting.title
                    : `1:1 ${meeting.otherUser.displayName}, ${meeting.leadUser.displayName}`}
                </strong>
                <> was started by </>
                {meeting.mostRecentInstance.startedByUser.displayName}
                <> at </>
                <span style={{ whiteSpace: "nowrap" }}>
                  {DateTime.fromISO(meeting.mostRecentInstance.startedAtDateTimeUtc, { zone: "utc" })
                    .toLocal()
                    .toLocaleString(DateTime.TIME_SIMPLE)}
                </span>

                {meeting.mostRecentInstance.endedByUser != null &&
                  meeting.mostRecentInstance.endedAtDateTimeUtc != null && (
                    <>
                      <> and ended by </>
                      {meeting.mostRecentInstance.endedByUser.displayName}
                      <> at </>
                      <span style={{ whiteSpace: "nowrap" }}>
                        {DateTime.fromISO(meeting.mostRecentInstance.endedAtDateTimeUtc, { zone: "utc" })
                          .toLocal()
                          .toLocaleString(DateTime.TIME_SIMPLE)}
                      </span>
                    </>
                  )}
              </Typography>
            </>
          )}

          <Button
            variant="contained"
            sx={{ alignSelf: "center" }}
            onClick={() => {
              if (meeting.type === MeetingType.Team) {
                navigate(Links.TeamMeetings(meeting.team.slug));
              }

              if (meeting.type === MeetingType.OneOnOne) {
                navigate(Links.UserMeetings(sessionUser.userId));
              }
            }}
          >
            Go To Meetings Page
          </Button>
        </Stack>
      </Box>
    );
  }

  return (
    <HeaderLayout
      title={
        <MeetingHeader
          meeting={meeting}
          meetingInstance={meeting.mostRecentInstance}
          participants={currentParticipants?.participants}
          isMeetingEnding={isMeetingEnding}
          externalCallUrl={meeting.externalCallUrl}
          onCloseMeetingClicked={handleCloseMeetingForAll}
        />
      }
      actions={
        <Stack direction="row" spacing={{ xs: 0.5, sm: 1 }}>
          <AddIssueOrTaskButton
            variant="contained"
            defaultSelectedTeamSlug={meeting?.type === MeetingType.Team ? meeting.team.slug : undefined}
          />
        </Stack>
      }
    >
      <Stack spacing={1}>
        <Stack direction="row" spacing={1}>
          <StickySideMenuCard sx={{ flex: 1 }}>
            <CardHeader
              title={
                <Stack direction="row" spacing={1} sx={{ justifyContent: "space-between" }}>
                  <Typography variant="h6">Agenda</Typography>
                  <MeetingNotesButton meetingGuid={meetingGuid} meetingType={meeting.type} />
                </Stack>
              }
              sx={{ pr: 0.5 }}
            />
            <Divider sx={{ borderColor: theme.palette.grey[200] }} />
            <Stepper orientation="vertical" activeStep={activeStepIndex} sx={{ p: 1 }}>
              {meeting.agendaItems
                .sort((a, b) => a.sortOrder.localeCompare(b.sortOrder))
                .map((item, index) => (
                  <Step key={item.name}>
                    <StepLabel>
                      <Stack direction="row" spacing={0.5}>
                        <Typography
                          variant="body2"
                          onClick={() => handleActiveAgendaStepChanged(index)}
                          sx={{
                            color: index === activeStepIndex ? theme.palette.primary.main : undefined,
                            cursor: "pointer",
                          }}
                        >
                          {item.name}
                        </Typography>
                        {item.allocatedTimeInMinutes > 0 && (
                          <Typography
                            variant="body1"
                            sx={{
                              color: "text.secondary"
                            }}
                          >{`${item.allocatedTimeInMinutes} min`}</Typography>
                        )}
                      </Stack>
                    </StepLabel>
                    <StepContent>
                      <Stack spacing={0.5}>
                        {meeting.type === MeetingType.OneOnOne && item.type === MeetingAgendaItemType.UserFeedback ? (
                          <UserFeedbackAgendaStepperContent agendaItem={item} meeting={meeting} />
                        ) : (
                          <TextViewer value={`<p>${item.description}</p>`} />
                        )}
                        <Stack direction="row" spacing={0.5}>
                          {index < meeting.agendaItems.length - 1 && (
                            <Button
                              variant="contained"
                              onClick={() => handleActiveAgendaStepChanged(activeStepIndex + 1)}
                            >
                              Next Step
                            </Button>
                          )}

                          {index > 0 && (
                            <Button
                              variant="outlined"
                              onClick={() => handleActiveAgendaStepChanged(activeStepIndex - 1)}
                            >
                              Back
                            </Button>
                          )}
                        </Stack>
                      </Stack>
                    </StepContent>
                  </Step>
                ))}
            </Stepper>
          </StickySideMenuCard>

          <Box sx={{ flex: 3, minWidth: 0 }}>
            {meeting.agendaItems.length > 0 && (
              <MeetingAgendaStepContent agendaStep={meeting.agendaItems[activeStepIndex]} meeting={meeting} />
            )}
          </Box>
        </Stack>
      </Stack>
    </HeaderLayout>
  );
};

export { MeetingPage };
